// Phase 9 — Dashboard-first UI rebalance. Home (dashboard) route.
//
// The primary landing surface. Composes:
//   1. TodayHero — pinned-today items from /api/daily-deck
//   2. ModuleGrid — one card per enabled module, equal weight
//
// States: loading (skeleton hero + skeleton grid), error (hero error card +
// fallback), empty (hero with "nothing urgent" + empty state), success.

import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import { useDashboard } from '../../hooks/useDashboard.js';
import { TodayHero } from './TodayHero.js';
import { ModuleGrid } from './ModuleGrid.js';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card.js';
import { SkeletonCard } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Button } from '../../components/ui/Button.js';

export function Home(): JSX.Element {
  React.useEffect(() => {
    document.title = 'DailyDeck';
  }, []);

  const query = useDashboard();

  return (
    <div className="space-y-8" data-testid="home-route">
      {/* Hero: today's urgent items. Independent of the dashboard query —
          it reads from /api/daily-deck via its own hook. */}
      <TodayHero />

      {/* Module grid: one card per enabled module. */}
      {query.isPending && (
        <section aria-label={copy.dashboard.loadingLabel}>
          <h2 className="text-title-3 font-semibold text-text-primary mb-4">
            Modules
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </section>
      )}

      {query.isError && (
        <Card data-testid="home-error">
          <CardHeader>
            <CardTitle>{copy.dashboard.errorTitle}</CardTitle>
            <CardDescription>{copy.errors.network_unavailable}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-body text-text-secondary">
              {copy.dashboard.errorBody}
            </p>
          </CardContent>
          <CardFooter>
            <Button
              variant="secondary"
              onClick={() => void query.refetch()}
            >
              {copy.dashboard.retry}
            </Button>
          </CardFooter>
        </Card>
      )}

      {query.isSuccess && query.data.length === 0 && (
        <EmptyState
          title={copy.dashboard.emptyTitle}
          description={copy.dashboard.emptyBody}
        />
      )}

      {query.isSuccess && query.data.length > 0 && (
        <ModuleGrid cards={query.data} />
      )}
    </div>
  );
}
